/**
 * A LIST THAT FORMATS ITSELF AS YOU TYPE — no toolbar, no rich-text menu.
 *
 * The card's write boxes — the "What is this about?" summary and the Updates &
 * comments composer — read the intent behind a newline and keep a list going.
 * Start a line with "- " (or "* ") or with "1." and, when you make a new line,
 * the next item's marker is written for you; the number counts on. Make a new
 * line on an item you left empty and the list ends: the marker is cleared and
 * you are on a plain line again. It writes the SAME plain "-"/number markup the
 * card already renders as a list (src/markdown.ts), so nothing new has to learn
 * to read it.
 *
 * This is the pure decision, with no DOM in it: given the text and where the
 * caret sits, it returns the text and caret AFTER the newline — or null when the
 * caret's line is not a list item, which tells the caller to let the newline
 * happen the ordinary way. Keeping it pure is what lets a test pin every case.
 *
 * WHICH KEY MAKES THE NEWLINE is the caller's to know, because it differs by
 * field: the summary box makes a newline on Enter (a plain textarea), while the
 * composer sends on Enter and makes its newline on Shift+Enter. Both hand the
 * work here; only the trigger differs.
 *
 * The marker patterns match the renderer's (src/markdown.ts BULLET/NUMBER) so a
 * continued list is one the card draws as a list, not stray dashes.
 */

export interface ListNewline {
  value: string;
  caret: number;
}

// A bullet ("-" or "*") or a number ("1." / "1)"), its own indent kept, then at
// least one space, then the item's text. The same shape src/markdown.ts reads.
const BULLET = /^(\s*)([-*])(\s+)(.*)$/;
const NUMBER = /^(\s*)(\d+)([.)])(\s+)(.*)$/;

/**
 * The text and caret after a newline that continues (or ends) a markdown list,
 * or null when there is no list to continue — in which case the caller lets the
 * newline fall through to the field's own handling.
 *
 * Acts only on a collapsed caret sitting at the END of its line: that is where a
 * person is when they finish an item and want the next one, and it keeps the
 * rule off a newline struck in the middle of a line (which stays ordinary).
 */
export function listNewline(text: string, selStart: number, selEnd: number): ListNewline | null {
  if (selStart !== selEnd) return null;
  const caret = selStart;

  const lineStart = text.lastIndexOf('\n', caret - 1) + 1;
  let lineEnd = text.indexOf('\n', caret);
  if (lineEnd === -1) lineEnd = text.length;
  // Only when the caret is at the end of its line — mid-line newlines stay plain.
  if (caret !== lineEnd) return null;

  const line = text.slice(lineStart, lineEnd);
  const bullet = BULLET.exec(line);
  const numbered = bullet ? null : NUMBER.exec(line);
  if (!bullet && !numbered) return null;

  const indent = bullet ? bullet[1] : numbered![1];
  const content = bullet ? bullet[4] : numbered![5];

  // An item left empty ends the list: clear this line's marker and leave the
  // caret on the now-blank line, ready for an ordinary paragraph. The blank line
  // is what tells the renderer the list is over.
  if (content.trim() === '') {
    return { value: text.slice(0, lineStart) + text.slice(lineEnd), caret: lineStart };
  }

  // A continued item: write the next marker on a fresh line, keeping the bullet
  // style (or counting the number on) and the item's own indent and spacing.
  const marker = bullet
    ? `${indent}${bullet[2]}${bullet[3]}`
    : `${indent}${parseInt(numbered![2], 10) + 1}${numbered![3]}${numbered![4]}`;
  const insert = `\n${marker}`;
  return { value: text.slice(0, caret) + insert + text.slice(caret), caret: caret + insert.length };
}
